import { useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/components/feature/Toast";
import SmartImage from "@/pages/seller/dashboard/components/SmartImage";
import ConfirmDialog from "@/pages/admin/dashboard/components/ConfirmDialog";
import EmptyState from "@/pages/admin/dashboard/components/EmptyState";
import {
  ANNOUNCEMENT_STATUS_META,
  missingAnnouncementFields,
  formatPrice,
  formatDate,
} from "@/pages/seller/dashboard/types";
import type { BadgeTone, SellerProduct } from "@/pages/seller/dashboard/types";

type FilterKey =
  | "all"
  | "draft"
  | "pending"
  | "active"
  | "paused"
  | "sold"
  | "rejected"
  | "archived";

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: "all", label: "Todos" },
  { key: "draft", label: "Borradores" },
  { key: "pending", label: "Pendientes" },
  { key: "active", label: "Disponibles" },
  { key: "paused", label: "Pausados" },
  { key: "sold", label: "Vendidos" },
  { key: "rejected", label: "Rechazados" },
  { key: "archived", label: "Archivados" },
];

const TONE_CLASSES: Record<BadgeTone, string> = {
  amber: "bg-accent-100 text-accent-800",
  green: "bg-secondary-100 text-secondary-700",
  indigo: "bg-primary-100 text-primary-700",
  neutral: "bg-background-100 text-foreground-600",
  red: "bg-accent-500 text-white",
  teal: "bg-secondary-500 text-white",
};

function AnnouncementBadge({ statusKey }: { statusKey: string }) {
  const meta = ANNOUNCEMENT_STATUS_META[statusKey] ?? ANNOUNCEMENT_STATUS_META.draft;
  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ${TONE_CLASSES[meta.tone]}`}
    >
      <i className={meta.icon}></i>
      {meta.label}
    </span>
  );
}

interface AnnouncementListProps {
  products: SellerProduct[];
  isAdmin: boolean;
  onEdit: (product: SellerProduct) => void;
  onRefresh: () => void;
}

export default function AnnouncementList({
  products,
  isAdmin,
  onEdit,
  onRefresh,
}: AnnouncementListProps) {
  const { showToast } = useToast();
  const [filter, setFilter] = useState<FilterKey>("all");
  const [deleting, setDeleting] = useState<SellerProduct | null>(null);
  const [busy, setBusy] = useState(false);

  // Determina el estado visual: un borrador incompleto se muestra como
  // "Pendiente de completar".
  function displayStatus(p: SellerProduct): string {
    if (p.status === "draft") {
      const missing = missingAnnouncementFields(p, isAdmin);
      return missing.length > 0 ? "pending_completion" : "draft";
    }
    return p.status;
  }

  const filtered = useMemo(() => {
    return products.filter((p) => {
      const s = displayStatus(p);
      switch (filter) {
        case "all":
          return true;
        case "draft":
          return s === "draft";
        case "pending":
          return (
            s === "pending_completion" ||
            s === "pending_moderation" ||
            s === "rejected"
          );
        case "active":
          return s === "active";
        case "paused":
          return s === "paused";
        case "sold":
          return s === "sold";
        case "rejected":
          return s === "rejected";
        case "archived":
          return s === "archived";
        default:
          return true;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, filter]);

  const counts = useMemo(() => {
    const c: Record<FilterKey, number> = {
      all: products.length,
      draft: 0,
      pending: 0,
      active: 0,
      paused: 0,
      sold: 0,
      rejected: 0,
      archived: 0,
    };
    products.forEach((p) => {
      const s = displayStatus(p);
      if (s === "draft") c.draft++;
      if (s === "pending_completion" || s === "pending_moderation" || s === "rejected")
        c.pending++;
      if (s === "active") c.active++;
      if (s === "paused") c.paused++;
      if (s === "sold") c.sold++;
      if (s === "rejected") c.rejected++;
      if (s === "archived") c.archived++;
    });
    return c;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  async function updateStatus(p: SellerProduct, status: string, message: string) {
    const { error } = await supabase
      .from("products")
      .update({
        status,
        published_at: status === "active" ? new Date().toISOString() : p.published_at,
      })
      .eq("id", p.id);
    if (error) {
      showToast(error.message, "error");
      return;
    }
    showToast(message, "success");
    onRefresh();
  }

  async function handleDuplicate(p: SellerProduct) {
    const { error } = await supabase.from("products").insert({
      seller_id: p.seller_id,
      title: `${p.title} (copia)`,
      description: p.description,
      price: p.price,
      currency: p.currency,
      category: p.category,
      category_id: p.category_id,
      condition: p.condition,
      brand: p.brand,
      model: p.model,
      negotiable: p.negotiable,
      min_price: p.min_price,
      quantity: p.quantity,
      province: p.province,
      municipality: p.municipality,
      sector: p.sector,
      images: p.images,
      status: "draft",
    });
    if (error) {
      showToast(error.message, "error");
      return;
    }
    showToast("Anuncio duplicado como borrador.", "success");
    onRefresh();
  }

  async function handleDelete() {
    if (!deleting) return;
    setBusy(true);
    const { error } = await supabase
      .from("products")
      .delete()
      .eq("id", deleting.id);
    setBusy(false);
    if (error) {
      showToast(error.message, "error");
      setDeleting(null);
      return;
    }
    showToast("Anuncio eliminado.", "success");
    setDeleting(null);
    onRefresh();
  }

  if (products.length === 0) {
    return (
      <EmptyState
        icon="ri-store-2-line"
        title="Aún no tienes anuncios"
        description="Crea tu primer anuncio desde «Crear anuncio» y aparecerá aquí."
      />
    );
  }

  return (
    <div>
      {/* Filtros */}
      <div className="flex flex-wrap gap-2 border-b border-background-200/70 px-5 py-4">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
              filter === f.key
                ? "bg-primary-500 text-white"
                : "bg-background-100 text-foreground-600 hover:bg-background-200"
            }`}
          >
            {f.label}
            <span
              className={`rounded-full px-1.5 text-[10px] ${
                filter === f.key ? "bg-white/25" : "bg-background-200"
              }`}
            >
              {counts[f.key]}
            </span>
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100">
            <i className="ri-inbox-line text-2xl text-foreground-400"></i>
          </span>
          <p className="mt-4 text-sm font-semibold text-foreground-900">
            No hay anuncios en este filtro
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 p-5 md:grid-cols-2 xl:grid-cols-3">
          {filtered.map((p) => {
            const statusKey = displayStatus(p);
            const missing = missingAnnouncementFields(p, isAdmin);
            const canPause = statusKey === "active";
            const canResume = statusKey === "paused";
            const canSell = ["active", "paused", "draft", "pending_completion", "pending_moderation"].includes(
              statusKey
            );
            const canArchive = ["active", "paused", "sold", "rejected"].includes(
              statusKey
            );
            return (
              <div
                key={p.id}
                className="overflow-hidden rounded-lg border border-background-200/70 bg-background-50 transition-colors hover:border-background-300"
              >
                {/* Imagen */}
                <div className="relative h-40 w-full overflow-hidden bg-background-100">
                  {p.images.length > 0 ? (
                    <SmartImage
                      src={p.images[0]}
                      alt={p.title}
                      className="h-full w-full object-cover object-top"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-foreground-300">
                      <i className="ri-image-line text-3xl"></i>
                    </div>
                  )}
                  <div className="absolute left-2 top-2">
                    <AnnouncementBadge statusKey={statusKey} />
                  </div>
                </div>

                {/* Contenido */}
                <div className="p-4">
                  <h3 className="truncate text-sm font-semibold text-foreground-900">
                    {p.title}
                  </h3>
                  <p className="mt-1 font-heading text-lg font-bold text-primary-600">
                    {formatPrice(p.price, p.currency)}
                  </p>

                  <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-foreground-500">
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-price-tag-3-line"></i>
                      {p.category ?? "Sin categoría"}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-stack-line"></i>
                      {p.quantity}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {p.views}
                    </span>
                  </div>

                  <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-foreground-400">
                    <span>Creado {formatDate(p.created_at)}</span>
                    {p.published_at && <span>Publicado {formatDate(p.published_at)}</span>}
                  </div>

                  {statusKey === "rejected" && p.rejection_reason && (
                    <p className="mt-2 rounded-md bg-accent-100/70 px-2.5 py-1.5 text-xs text-accent-800">
                      Motivo: {p.rejection_reason}
                    </p>
                  )}

                  {statusKey === "pending_completion" && (
                    <p className="mt-2 text-xs text-foreground-500">
                      <span className="font-semibold">Falta:</span>{" "}
                      {missing.join(", ")}
                    </p>
                  )}

                  {/* Acciones */}
                  <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-background-200/70 pt-3">
                    <button
                      type="button"
                      onClick={() => onEdit(p)}
                      className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-2.5 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-primary-600"
                    >
                      <i className="ri-edit-line"></i>
                      {statusKey === "draft" || statusKey === "pending_completion"
                        ? "Continuar"
                        : "Editar"}
                    </button>

                    {canPause && (
                      <ActionIcon
                        icon="ri-pause-circle-line"
                        label="Pausar"
                        onClick={() =>
                          updateStatus(p, "paused", "Anuncio pausado.")
                        }
                      />
                    )}
                    {canResume && (
                      <ActionIcon
                        icon="ri-play-circle-line"
                        label="Activar"
                        onClick={() =>
                          updateStatus(p, "active", "Anuncio activado nuevamente.")
                        }
                      />
                    )}
                    {canSell && (
                      <ActionIcon
                        icon="ri-hand-coin-line"
                        label="Marcar vendido"
                        onClick={() =>
                          updateStatus(p, "sold", "Anuncio marcado como vendido.")
                        }
                      />
                    )}
                    {canArchive && (
                      <ActionIcon
                        icon="ri-archive-line"
                        label="Archivar"
                        onClick={() =>
                          updateStatus(p, "archived", "Anuncio archivado.")
                        }
                      />
                    )}
                    <ActionIcon
                      icon="ri-file-copy-line"
                      label="Duplicar"
                      onClick={() => handleDuplicate(p)}
                    />
                    <ActionIcon
                      icon="ri-delete-bin-line"
                      label="Eliminar"
                      danger
                      onClick={() => setDeleting(p)}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        open={!!deleting}
        title="¿Eliminar anuncio?"
        description={`Se eliminará «${deleting?.title ?? ""}» de forma permanente. Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar"
        tone="danger"
        loading={busy}
        onConfirm={handleDelete}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
}

function ActionIcon({
  icon,
  label,
  onClick,
  danger,
}: {
  icon: string;
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={`flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-background-200 text-foreground-500 transition-colors ${
        danger
          ? "hover:border-accent-300 hover:bg-accent-100 hover:text-accent-700"
          : "hover:border-primary-300 hover:bg-primary-100 hover:text-primary-700"
      }`}
    >
      <i className={icon}></i>
    </button>
  );
}